import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Badge } from 'components/ui/Badge'
import { applicationsApi, type Application, type Team } from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import {
  Search,
  Trash2,
  Mail,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { getStatusColor } from 'lib/utils'

interface ManageCandidatesModalProps {
  isOpen: boolean
  onClose: () => void
  offerId: number
  teams: Team[]
  onApplicationsUpdated: () => void
}

const PAGE_SIZE = 10

export function ManageCandidatesModal({
  isOpen,
  onClose,
  offerId,
  teams,
  onApplicationsUpdated
}: ManageCandidatesModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { addToast } = useToast()
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())
  const [candidateToDelete, setCandidateToDelete] = useState<{ id: number; name: string } | null>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const getGroupsForApplication = (app: Application): string[] => {
    const groupNames: string[] = []

    const candidateTeams = teams.filter(team =>
      team.candidates?.some(c => c.id === app.candidate)
    )
    candidateTeams.forEach(team => {
      if (!groupNames.includes(team.name)) {
        groupNames.push(team.name)
      }
    })

    if (app.notes) {
      if (app.notes.includes('Added from groups:')) {
        const groupsPart = app.notes.split('Added from groups:')[1]?.trim()
        if (groupsPart) {
          const noteGroups = groupsPart.split(',').map(g => g.trim()).filter(g => g.length > 0)
          noteGroups.forEach(groupName => {
            if (!groupNames.includes(groupName)) {
              groupNames.push(groupName)
            }
          })
        }
      } else if (app.notes.includes('Added from newly created group:')) {
        const groupName = app.notes.split('Added from newly created group:')[1]?.trim()
        if (groupName && !groupNames.includes(groupName)) {
          groupNames.push(groupName)
        }
      }
    }

    return groupNames
  }

  const fetchApplications = useCallback(async (page: number, search: string) => {
    if (!offerId) return
    setLoading(true)
    try {
      const response = await applicationsApi.getByOffer(offerId, {
        status: 'applied',
        page,
        page_size: PAGE_SIZE,
        summary: true,
        search: search || undefined,
        ordering: '-created_at',
      })
      setApplications(response.data.applications || [])
      setTotalCount(response.data.count ?? response.data.total_count ?? 0)
      setHasMore(!!response.data.has_more)
      setCurrentPage(response.data.page || page)
    } catch (error) {
      console.error('Error fetching applications for manage modal:', error)
      setApplications([])
      setTotalCount(0)
      setHasMore(false)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.loadCandidatesFailed', 'Failed to load candidates. Please try again.'),
      })
    } finally {
      setLoading(false)
    }
  }, [offerId, addToast, t])

  useEffect(() => {
    if (!isOpen) {
      setCandidateToDelete(null)
      setSearchTerm('')
      setDebouncedSearch('')
      setCurrentPage(1)
      setApplications([])
      setTotalCount(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
      setCurrentPage(1)
    }, 300)
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [searchTerm])

  useEffect(() => {
    if (!isOpen) return
    fetchApplications(currentPage, debouncedSearch)
  }, [isOpen, currentPage, debouncedSearch, fetchApplications])

  const handleDeleteCandidate = async () => {
    if (!candidateToDelete) return

    const { id: applicationId, name: candidateName } = candidateToDelete

    setDeletingIds(prev => new Set(prev).add(applicationId))

    try {
      await applicationsApi.delete(applicationId)
      addToast({
        type: 'success',
        title: t('offerDetail.candidateRemoved', 'Candidate removed'),
        message: t('offerDetail.candidateRemovedMessage', {
          name: candidateName,
          defaultValue: `${candidateName} has been removed from this offer.`
        })
      })
      onApplicationsUpdated()
      setCandidateToDelete(null)

      const remainingOnPage = applications.length - 1
      const nextPage = remainingOnPage <= 0 && currentPage > 1 ? currentPage - 1 : currentPage
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage)
      } else {
        await fetchApplications(nextPage, debouncedSearch)
      }
    } catch (error) {
      console.error('Error deleting application:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.deleteCandidateFailed', 'Failed to remove candidate. Please try again.')
      })
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev)
        next.delete(applicationId)
        return next
      })
    }
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  const isDeletingSelectedCandidate = candidateToDelete ? deletingIds.has(candidateToDelete.id) : false

  const handleCloseDeleteModal = () => {
    if (isDeletingSelectedCandidate) return
    setCandidateToDelete(null)
  }

  const handleCandidateNamePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation()
  }

  const handleCandidateNameClick = (event: React.MouseEvent<HTMLButtonElement>, candidateId: number) => {
    event.stopPropagation()
    event.preventDefault()
    onClose()
    navigate(`/candidates/${candidateId}`, { state: { from: currentPath } })
  }

  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const showingTo = Math.min(currentPage * PAGE_SIZE, totalCount)

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('offerDetail.manageCandidates', 'Manage Candidates')}
        size="lg"
      >
        <div className="flex flex-col h-full">
        <div className="space-y-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('offerDetail.searchCandidates', 'Search candidates by name, email, or groups...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 mt-4">
          <div className="space-y-2 max-h-64 overflow-y-auto relative min-h-[120px]">
          {loading && (
            <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-lg">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loading && applications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground h-full flex items-center justify-center">
              {searchTerm ? (
                <p>{t('offerDetail.noCandidatesFound', 'No candidates found matching your search.')}</p>
              ) : (
                <p>{t('offerDetail.noCandidates', 'No candidates in this offer.')}</p>
              )}
            </div>
          ) : (
            applications.map((app) => {
              const isDeleting = deletingIds.has(app.id)
              const statusColor = getStatusColor(app.status || 'applied')
              const groups = getGroupsForApplication(app)
              const groupTags = groups.map(groupName => {
                const team = teams.find(tm => tm.name === groupName)
                return {
                  name: groupName,
                  color: team?.color || '#007bff'
                }
              })

              return (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 overflow-hidden">
                      {app.candidate_profile_picture ? (
                        <img
                          src={app.candidate_profile_picture}
                          alt={app.candidate_name || 'Candidate'}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        (app.candidate_name || app.candidate_email || 'C')
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1 flex-wrap">
                        <button
                          type="button"
                          onPointerDown={handleCandidateNamePointerDown}
                          onClick={(event) => handleCandidateNameClick(event, app.candidate)}
                          className="font-medium text-left text-blue-600 dark:text-blue-300 truncate hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                          title={app.candidate_name || 'Unknown'}
                        >
                          {app.candidate_name || 'Unknown'}
                        </button>
                        <Badge
                          variant="outline"
                          className={`text-xs ${statusColor}`}
                        >
                          {app.status || 'applied'}
                        </Badge>
                        {groupTags.length > 0 && groupTags.map((tag, idx) => {
                          const hexToRgb = (hex: string) => {
                            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
                            return result ? {
                              r: parseInt(result[1], 16),
                              g: parseInt(result[2], 16),
                              b: parseInt(result[3], 16)
                            } : null
                          }

                          const rgb = hexToRgb(tag.color)
                          const luminance = rgb ? (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 : 0.5
                          const textColor = luminance > 0.5 ? '#000000' : '#ffffff'

                          return (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0.5 border-0"
                              style={{
                                backgroundColor: tag.color,
                                color: textColor
                              }}
                            >
                              {tag.name}
                            </Badge>
                          )
                        })}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground flex-wrap">
                        <div className="flex items-center space-x-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{app.candidate_email || 'N/A'}</span>
                        </div>
                        {app.applied_date && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(app.applied_date)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCandidateToDelete({
                      id: app.id,
                      name: app.candidate_name || 'Candidate'
                    })}
                    disabled={isDeleting}
                    className="ml-4 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                  >
                    {isDeleting ? (
                      <div className="h-4 w-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )
            })
          )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t flex-shrink-0 mt-4 gap-3 flex-wrap">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              {totalCount > 0
                ? `${t('common.showing')} ${showingFrom}-${showingTo} ${t('common.of')} ${totalCount}`
                : t('offerDetail.noCandidates', 'No candidates in this offer.')}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || loading}
                  title={t('common.firstPage', 'First page')}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  title={t('common.previous', 'Previous')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs font-bold text-foreground px-2 min-w-[3rem] text-center">
                  {currentPage}/{totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={!hasMore || currentPage >= totalPages || loading}
                  title={t('common.next', 'Next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  title={t('common.lastPage', 'Last page')}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!candidateToDelete}
        onClose={handleCloseDeleteModal}
        title={t('offerDetail.deleteCandidate', 'Delete Candidate')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('offerDetail.confirmDeleteCandidate', {
              name: candidateToDelete?.name,
              defaultValue: `Are you sure you want to remove ${candidateToDelete?.name || 'this candidate'} from this offer?`
            })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={handleCloseDeleteModal}
              disabled={isDeletingSelectedCandidate}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCandidate}
              disabled={isDeletingSelectedCandidate}
            >
              {t('offerDetail.deleteCandidate', 'Delete Candidate')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
